import type { LinkPreviewProps } from 'Components/Chat/ChatLayout/LinkPreview/index.types';
import { TIMEZONE_IDENTIFIER } from 'Constants/env';
import { isEmpty } from 'lodash';
import { action, computed, observable, makeObservable } from 'mobx';
import moment from 'moment-timezone';
import { CALL_DIRECTION, CALL_DISPOSITION } from '../constants/enums';

export interface IMessageReactions {
  [emoji: string]: string[];
}

export type MessageReactions = IMessageReactions;

export interface IMessageGifPayload {
  id: string;
}

export interface IMessageModelChat {
  text: string;
  text_v2?: string;
  smsRelay?: IMessageModelSMSRelay;
}
export interface IMessageModelSMSRelay {
  smsId?: string;
  fromPhone?: string;
  toPhone?: string;
  sent?: string;
}

export interface IMessageModelSmsPhoneStatus {
  toPhone: string;
  status: 'Unknown' | 'Sending' | 'Delivered' | 'Received' | 'FailedToDeliver';
}

export interface IMessageModelSMS {
  text: string;
  smsId?: string;
  fromPhone?: string;
  newSmsServiceToPhones?: IMessageModelSmsPhoneStatus[];
}

export type MessageModelSmsCalculatedStatus =
  | 'Sending'
  | 'FailedToSend'
  | 'Sent'
  | 'Delivered'
  | 'FailedToDeliver'
  | 'FailedToDeliverGroup';

export type MessageModelInternalStatus = 'Sending' | 'FailedToSend' | 'Sent';

export interface IMessageModelConferencePersonId
  extends IMessageModelConference {
  personId: number;
}
export interface IMessageModelConference {
  id: string;
  sessionId?: string;
  adminId: number;
  provider?: string;
  phone?: string;
  url?: string;
  start?: string;
  end?: string;
  type?: string;
  recordingUrl?: string;
  recordingDuration?: string;
  attendees: IMessageModelConferenceAttendees[];
  hasRecording: boolean;
  duration: string;
  displayName: string;
}

export interface IMessageModelSystemEvent {
  eventType: string;
  reference: string;
  source: string;
  payload: {
    accountId?: number;
    personId?: number;
    conversationId?: string;
    conferenceId?: string;
    messageId?: string;
    url?: string;
    numberOfParticipants?: number;
    conferenceDuration?: string;
    conferenceRecorded?: boolean;
    participants?: { personId: number; phone: string }[];
    participantsAdded?: { personId: number; phone: string }[];
    participantsRemoved?: { personId: number; phone: string }[];
  };
}

export interface IMessageModelConferenceAttendees {
  id: string;
  personId?: string;
  phone?: string;
  name?: string;
  email?: string;
  ip?: string;
  start?: Date;
  end?: Date;
}

export interface IMessageModelCall {
  direction: CALL_DIRECTION;
  from: {
    friendlyName: string;
    phone: string;
    personId: number;
  };
  to: {
    friendlyName: string;
    phone: string;
    personId: number;
  };
  callId: string;
  start: string;
  bridged: string;
  end: string;
  totalDuration: string;
  callDuration: string;
  disposition: CALL_DISPOSITION;
}

export interface IMessageModel {
  id: string;
  /** External FK, mutually exclusive with `phone` */
  personId?: number;
  /** Mutually exclusive with `personId` */
  phone?: string;
  created: string;
  updated?: string;
  chat?: IMessageModelChat;
  gif?: IMessageGifPayload;
  documents?: IMessageDocument[];
  sms?: IMessageModelSMS;
  call?: IMessageModelCall;
  conference?: IMessageModelConference;
  systemEvent?: IMessageModelSystemEvent;
  isDeleted?: boolean;
  references?: IMessageReferences[];
  reactions?: IMessageReactions;

  /** Marker for a message received from Pusher, allowing for different behavior if needed. */
  __isPush?: boolean;
}

export type IMessageReferences = {
  id: string;
  type: 'reply';
  snapshot: {
    chat?: IMessageModelChat;
    created: string;
    documents?: IMessageDocument[];
    personId?: number;
    gif?: IMessageGifPayload;
  };
};

export interface IMessageLinkPreview {
  url: string;
  image?: string;
  title?: string;
  description?: string;
}

export interface IMessageLinkPreviewDimensions {
  /** Height of the entire `<LinkPreview />` */
  width: number;
  /** Width of the entire `<LinkPreview />` */
  height: number;
  /** Height of the image within the `<LinkPreview />` */
  imageWidth: number;
  /** Width of the image within the `<LinkPreview />` */
  imageHeight: number;
  imageLoadFailed?: boolean;
}

export interface IMessageDocument {
  externalId: string;
  contentType?: string;
  description?: string;
  byteCount?: number;
  created?: string;
  isDeleted: boolean;
  url: string;
  hasPreview?: boolean;
  previewContentType?: string;
  previewUrl?: string;
  previewHeight?: number;
  previewWidth?: number;
  name?: string;
}

export interface IMessageModelConstructorProps {
  id: string;
  created: string;
  updated?: string;
  personId?: number;
  phone?: string;
  chat?: IMessageModelChat;
  gif?: IMessageGifPayload;
  documents?: IMessageDocument[];
  sms?: IMessageModelSMS;
  call?: IMessageModelCall;
  conference?: IMessageModelConference;
  systemEvent?: IMessageModelSystemEvent;
  isDeleted?: boolean;
  isPush?: boolean;
  references?: IMessageReferences[];
  internalStatus?: MessageModelInternalStatus;
  reactions?: IMessageReactions;
}

export class MessageModel implements IMessageModel {
  @observable
  public id: string;

  @action
  setId = (id: string) => (this.id = id);

  /** External FK, mutually exclusive with `phone` */
  public personId?: number;
  /** Mutually exclusive with `personId` */
  public phone?: string;

  public isFirstIn5Mins?: boolean;

  /** Created Time ISO8601 UTC */
  @observable
  public created: string;

  /** Set the Created Time ISO8601 UTC */
  @action
  public setCreated = (created: string) => (this.created = created);

  /** Set the smsRelay if it exists */
  @action
  public setSMSRelay = (smsRelay: IMessageModelSMSRelay) => {
    this.chat.smsRelay = smsRelay;
  };

  @observable
  public isRead: boolean;

  @action
  setIsRead = (isRead: boolean) => (this.isRead = isRead);

  /** Zoned `moment` representation of `created` (zoned to `TIMEZONE_IDENTIFIER`) */
  @computed
  get CreatedMoment() {
    return moment.tz(this.created, TIMEZONE_IDENTIFIER);
  }

  public updated: string;
  @action
  setUpdated = (updated: string) => (this.updated = updated);

  @observable
  public isDeleted?: boolean;

  @action
  setDeleted = (deleted: boolean) => (this.isDeleted = deleted);

  @observable
  public chat?: IMessageModelChat;

  @action
  setChat = (chat: IMessageModelChat) => (this.chat = chat);

  @observable
  public gif?: IMessageGifPayload;

  @action
  setGif = (gif: IMessageGifPayload) => (this.gif = gif);

  @observable
  public sms?: IMessageModelSMS;

  @action
  setSms = (sms: IMessageModelSMS) => (this.sms = sms);

  @observable
  public call?: IMessageModelCall;

  @observable
  public documents: IMessageDocument[];

  @action
  setDocuments = (documents: IMessageDocument[]) =>
    (this.documents = documents);

  @action
  setDocument = (externalId: string) => {
    const filteredData = this.documents.reduce((prevVal, document) => {
      if (document.externalId === externalId) {
        prevVal.push({ ...document, isDeleted: true });
        return prevVal;
      }
      prevVal.push(document);
      return prevVal;
    }, []);
    this.setDocuments(filteredData);
  };

  @action
  setCall = (call: IMessageModelCall) => (this.call = call);

  @observable
  public conference?: IMessageModelConference;

  @action
  setConference = (conference: IMessageModelConference) =>
    (this.conference = conference);

  @observable
  public references?: IMessageReferences[];

  @action
  setReferences = (references: IMessageReferences[]) =>
    (this.references = references);

  @observable
  public systemEvent?: IMessageModelSystemEvent;

  @action
  setSystemEvent = (systemEvent: IMessageModelSystemEvent) =>
    (this.systemEvent = systemEvent);

  //
  // --- *UI only properties, which do not correspond to API DTO responses ---
  //
  @observable
  public showKeyPad = false;

  @action
  setShowKeyPad = (showKeyPad: boolean) => (this.showKeyPad = showKeyPad);

  @action
  toggleShowKeyPad = () => (this.showKeyPad = !this.showKeyPad);

  @observable
  public userNumberInput = '';

  @action
  appendUserNumberInput = (inputKey: string) =>
    (this.userNumberInput += inputKey);

  @action
  deleteUserNumberInput = () =>
    (this.userNumberInput = this.userNumberInput.slice(
      0,
      this.userNumberInput.length - 1
    ));

  @observable
  public showIcon = false;

  @action
  setShowIcon = (showIcon: boolean) => (this.showIcon = showIcon);

  @action
  toggleShowIcon = () => (this.showIcon = !this.showIcon);

  @observable
  linkPreview: LinkPreviewProps = null;

  @action
  setLinkPreview = (obj: LinkPreviewProps) => {
    if (isEmpty(obj)) {
      this.linkPreview = null;
    } else {
      this.linkPreview = obj;
    }
  };

  @observable
  linkPreviewDimensions: IMessageLinkPreviewDimensions = null;

  @action
  setLinkPreviewImageDimensions = (
    dimensions: IMessageLinkPreviewDimensions
  ) => {
    if (isEmpty(dimensions)) {
      this.linkPreviewDimensions = null;
    } else {
      this.linkPreviewDimensions = dimensions;
    }
  };

  //
  // *--- End UI only properties, which do not correspond to API DTO responses ---
  //

  private _createdDate: Date;
  public get CreatedAtDate() {
    return this._createdDate || (this._createdDate = new Date(this.created));
  }

  @action
  setCreatedAtDate = (createdAtDate: Date) =>
    (this._createdDate = createdAtDate);

  /** Source for the next `pendingSortSequence`. Only ever increments. */
  private static _pendingSequenceCounter = 0;

  /**
   * Monotonically increasing local sequence used to order optimistic ("pending")
   * messages relative to one another.
   */
  @observable
  public pendingSortSequence?: number = undefined;

  /**
   * True while this message has only a locally-generated `id` and has not yet been
   * acknowledged by the server.
   */
  @computed
  get isPending() {
    return this.pendingSortSequence !== undefined && this.pendingSortSequence !== null;
  }

  /**
   * Mark this message as an optimistic/pending send, assigning it the next local
   * send sequence. Call this when creating a message locally, before the server
   * has assigned an authoritative `id`.
   */
  @action
  markPending = () => {
    this.pendingSortSequence = ++MessageModel._pendingSequenceCounter;
  };

  /**
   * Adopt the server-authoritative `id` and `created` timestamp and clear the
   * pending marker. After this the message is ordered by its server-generated
   * TimeUUID like every other message, independent of the local clock.
   */
  @action
  acknowledgeServer = (id: string, created: string) => {
    this.id = id;
    if (!isEmpty(created)) {
      this.created = created;
      this._createdDate = new Date(created);
    }
    this.pendingSortSequence = undefined;
  };

  /**
   * Marker for a message received from Pusher, allowing for different behavior if needed.
   * _This field should be filtered out of API requests._
   */
  public __isPush = false;

  @observable
  public failedToSendSms?: boolean;

  @action
  setFailedToSendSms = (failedToSendSms: boolean) =>
    (this.failedToSendSms = failedToSendSms);

  @computed
  get smsStatus(): MessageModelSmsCalculatedStatus | undefined {
    if (this.chat || this.isDeleted || this.internalStatus !== undefined) {
      return undefined;
    }

    if (this.failedToSendSms) {
      return 'FailedToSend';
    }

    // If there is no newSmsServiceToPhones info, it means we're waiting for the response from the service.
    if (
      !this.sms?.newSmsServiceToPhones ||
      this.sms.newSmsServiceToPhones.length === 0
    ) {
      return 'Sending';
    }

    const allDelivered = this.sms.newSmsServiceToPhones.every(
      (phoneStatus) => phoneStatus.status === 'Delivered'
    );

    if (allDelivered) {
      return 'Delivered';
    }

    const allFailedToDeliver = this.sms.newSmsServiceToPhones.every(
      (phoneStatus) => phoneStatus.status === 'FailedToDeliver'
    );

    if (allFailedToDeliver) {
      return 'FailedToDeliver';
    }

    const anyFailedToDeliver = this.sms.newSmsServiceToPhones.some(
      (phoneStatus) => phoneStatus.status === 'FailedToDeliver'
    );

    // No need to check for length here. If there's only one phone, and it failed to deliver, it was already caught above.
    if (anyFailedToDeliver) {
      return 'FailedToDeliverGroup';
    }

    return 'Sent';
  }

  @observable
  public internalStatus?: MessageModelInternalStatus;

  @action
  setInternalStatus = (internalStatus: MessageModelInternalStatus) =>
    (this.internalStatus = internalStatus);

  @observable
  public reactions?: IMessageReactions;

  @action
  setReactions = (reactions: IMessageReactions) => {
    this.reactions = reactions;
  };

  constructor({
    id,
    created,
    updated,
    personId,
    phone,
    chat,
    gif,
    documents,
    sms,
    call,
    conference,
    systemEvent,
    isDeleted,
    isPush,
    references,
    internalStatus,
    reactions,
  }: IMessageModelConstructorProps) {
    makeObservable(this);
    if (personId && phone) {
      this.personId = personId;
      this.phone = null;
    } else {
      this.personId = personId;
      this.phone = phone;
    }
    if (
      (!chat &&
        !gif &&
        !call &&
        !sms &&
        !conference &&
        !systemEvent &&
        !documents &&
        isDeleted !== true) ||
      (chat && call && sms && conference && documents && systemEvent)
    ) {
      throw Error(
        "Either a chat, call, conference, or event is required, not all can be provided, and all can't be null/empty/0."
      );
    }
    this.id = id;
    this.created = created;
    this.updated = updated;
    this._createdDate = new Date(this.created);
    this.chat = chat;
    this.gif = gif;
    this.sms = sms;
    this.call = call;
    this.documents = documents;
    this.conference = conference;
    this.systemEvent = systemEvent;
    this.isDeleted = isDeleted;
    this.__isPush = isPush;
    this.references = references;
    /**
     * If the status was provided, use it.
     * Otherwise, if the message is a chat message, set it to Sent by default.
     * Otherwise, set it to undefined.
     */
    this.internalStatus =
      internalStatus ||
      (chat || gif || documents?.length > 0 ? 'Sent' : undefined);
    this.reactions = reactions;
  }

  static FromResponseDto(responseDto: IMessageModel) {
    return new MessageModel({
      id: responseDto.id,
      created: responseDto.created,
      updated: responseDto.updated,
      personId: responseDto.personId,
      phone: responseDto.phone,
      chat: responseDto.chat,
      gif: responseDto.gif,
      documents: responseDto.documents,
      sms: responseDto.sms,
      call: responseDto.call,
      conference: responseDto.conference,
      systemEvent: responseDto.systemEvent,
      isDeleted: responseDto.isDeleted,
      isPush: null,
      references: responseDto.references,
      reactions: responseDto.reactions,
    });
  }
}

export default MessageModel;
